import {APP_CONFIG, CURRENCY_POSITIONS} from '../config';
import type {CurrencyPosition} from '../config';

type CurrencyFormatOptions = {
  symbol?: string;
  position?: CurrencyPosition;
  fractionDigits?: number;
};

const DEFAULT_FRACTION_DIGITS = 2;
const DEFAULT_POSITION: CurrencyPosition = 'left';

const isValidPosition = (
  position: CurrencyPosition | undefined,
): position is CurrencyPosition => {
  if (!position) {
    return false;
  }

  return (CURRENCY_POSITIONS as readonly string[]).includes(position);
};

const resolveFractionDigits = (fractionDigits?: number) => {
  if (typeof fractionDigits === 'number' && fractionDigits >= 0) {
    return fractionDigits;
  }

  return DEFAULT_FRACTION_DIGITS;
};

const resolvePosition = (position?: CurrencyPosition) => {
  if (isValidPosition(position)) {
    return position;
  }

  const configuredPosition = APP_CONFIG.currency?.position;

  if (isValidPosition(configuredPosition)) {
    return configuredPosition;
  }

  return DEFAULT_POSITION;
};

const resolveSymbol = (symbol?: string) => {
  if (typeof symbol === 'string' && symbol.trim()) {
    return symbol.trim();
  }

  const configuredSymbol = APP_CONFIG.currency?.symbol;

  if (typeof configuredSymbol === 'string' && configuredSymbol.trim()) {
    return configuredSymbol.trim();
  }

  return APP_CONFIG.checkout?.defaultCurrency ?? 'DHS';
};

const applyCurrencyPosition = (
  amount: string,
  symbol: string,
  position: CurrencyPosition,
) => {
  switch (position) {
    case 'left-spaced':
      return `${symbol} ${amount}`;
    case 'right':
      return `${amount}${symbol}`;
    case 'right-spaced':
      return `${amount} ${symbol}`;
    case 'left':
    default:
      return `${symbol}${amount}`;
  }
};

const formatAmount = (value: number | string, fractionDigits: number) => {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue.toFixed(fractionDigits);
  }

  return `${value}`;
};

export const formatCurrency = (
  value: number | string,
  options: CurrencyFormatOptions = {},
) => {
  const fractionDigits = resolveFractionDigits(options.fractionDigits);
  const symbol = resolveSymbol(options.symbol);
  const position = resolvePosition(options.position);
  const amount = formatAmount(value, fractionDigits);

  return applyCurrencyPosition(amount, symbol, position);
};

export const getCurrencySymbol = () => resolveSymbol();

export const getCurrencyPosition = () => resolvePosition();

